import { getConfig } from "../config";
import { getIdToken } from "./auth";

export class ApiError extends Error {
  constructor(
    /** 0 means the request never reached the server at all -- see NETWORK_ERROR_STATUS. */
    public status: number,
    message: string,
    /**
     * The parsed error body, when there was one, so a caller can render more than the sentence.
     *
     * Some refusals carry the facts behind them: declining to delete a library entry returns the plans
     * still using it, and "this exercise is in use" without them would send the coach hunting through
     * every plan. Undefined when the body was absent or not JSON, so callers must treat it as optional.
     */
    public details?: Record<string, unknown>
  ) {
    super(message);
  }
}

/** No HTTP status exists for "the request never got there", and 0 is what fetch-adjacent code conventionally uses. */
const NETWORK_ERROR_STATUS = 0;

/**
 * How long anything here is allowed to hang before it counts as unreachable.
 *
 * Nothing in this file had a timeout, and the result was the worst failure mode in the review: with the
 * connection dead the app span forever and never said why -- "Loading your account…", then "Still
 * working on it", then nothing, still spinning at 55 seconds (UX review F89). An indefinite spinner
 * reads as "this app is broken" rather than "you have no signal", and the difference decides whether
 * someone tries again later.
 *
 * Long enough not to give up on a slow rink 4G connection, short enough that a person still gets an
 * answer while they are looking at the screen.
 */
const REQUEST_TIMEOUT_MS = 12_000;

/**
 * The offline message, phrased from what the browser actually knows.
 *
 * `navigator.onLine` being false is a definite answer and worth saying plainly; true only means an
 * interface is up, which inside a rink is routinely a lie, so that case stays hedged.
 */
function unreachableMessage(): string {
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  return offline
    ? "You appear to be offline. Check your connection and try again."
    : "Couldn't reach the server. Check your connection and try again.";
}

/**
 * Rejects rather than hanging forever.
 *
 * Wrapped around the token read specifically because that is where the app actually hung: `getIdToken`
 * calls into Cognito's `getSession`, which silently performs a *network* refresh when the ID token has
 * expired. With no connection that call never settles and never times out, so the request never even
 * reached `fetch` -- which is why none of the careful offline handling below ever ran.
 */
function withTimeout<T>(work: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new ApiError(NETWORK_ERROR_STATUS, unreachableMessage())), ms);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/**
 * Turns an error response body into something worth showing a person.
 *
 * Every handler answers with `JSON.stringify({ message })`, and `ErrorState` renders `error.message`
 * straight into the page -- so passing the raw body through put the literal string
 * `{"message":"Only a coach can create plans"}` on screen. The message inside is the one actually
 * written for a human.
 *
 * Falls back by status rather than showing a bare status code, because the cases where a body is empty
 * or unrecognised are exactly the ones where the user has least to go on.
 */
function errorMessageFrom(body: string, status: number): string {
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed && typeof parsed === "object" && typeof (parsed as { message?: unknown }).message === "string") {
      return (parsed as { message: string }).message;
    }
  } catch {
    // Not JSON. A plain-text body is still better than a generic message, so fall through to it below.
  }

  // Only trust a non-JSON body if it isn't itself JSON-looking -- otherwise an unrecognised JSON shape
  // would be shown verbatim, which is the thing this function exists to prevent.
  const looksLikeJson = body.trimStart().startsWith("{") || body.trimStart().startsWith("[");
  if (body && !looksLikeJson) return body;

  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "We couldn't find that.";
  if (status === 409) return "That changed while you were working on it. Reload and try again.";
  return "Something went wrong on our end. Please try again.";
}

/** The error body as an object, or undefined if it was absent, not JSON, or JSON but not an object. */
function parsedObject(body: string): Record<string, unknown> | undefined {
  try {
    const parsed: unknown = JSON.parse(body);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

async function request<T>(method: string, path: string, body?: unknown, allowAnonymous = false): Promise<T> {
  // Bounded, because this is a network call in disguise -- see withTimeout.
  const token = await withTimeout(getIdToken(), REQUEST_TIMEOUT_MS);
  // No token at all means the Cognito session is gone (expired refresh token, signed out in another
  // tab). Failing here rather than firing an Authorization-less request keeps the 401 shape identical
  // whether the session died client-side or server-side, so callers only handle one case.
  //
  // Except on the public endpoints (`allowAnonymous`), where "no session" is the *normal* state rather
  // than a failure: an invitee looking up their invite has never had an account. Applying the rule
  // there made every real invite link render "Invite not found" while the API answered 200 to the same
  // request unauthenticated -- see UX review F60.
  if (!token && !allowAnonymous) {
    throw new ApiError(401, "Your session has expired. Please sign in again.");
  }

  let response: Response;
  // Aborts a request that is neither failing nor arriving. A connection that accepts a socket and then
  // goes quiet -- a captive portal, a rink dead spot -- leaves fetch pending indefinitely, and without
  // this the page waits on it for as long as the user is willing to.
  const controller = new AbortController();
  const abortTimer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    response = await fetch(`${getConfig().apiBaseUrl}${path}`, {
      method,
      headers: {
        ...(token ? { Authorization: token } : {}),
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch {
    // fetch rejects with a bare "TypeError: Failed to fetch" when the network is down, the request is
    // blocked, or DNS fails -- a string that reads like a bug in the app rather than a connection
    // problem. Wrapping it also means every failure a caller sees is an ApiError, so code branching on
    // `instanceof ApiError` doesn't silently miss the offline case. An abort from the timeout above
    // lands here too, which is right: from the caller's point of view it is the same failure.
    throw new ApiError(NETWORK_ERROR_STATUS, unreachableMessage());
  } finally {
    clearTimeout(abortTimer);
  }

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, errorMessageFrom(text, response.status), parsedObject(text));
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>("GET", path);
}

/**
 * A GET that works with or without a session, for the endpoints the API deliberately leaves public.
 *
 * Only `/invites/by-token/{token}` needs this today. Kept as a separate function rather than an option
 * on `apiGet` so that reaching for it is a deliberate act: everything else in the app must fail closed.
 */
export function apiGetPublic<T>(path: string): Promise<T> {
  return request<T>("GET", path, undefined, true);
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("POST", path, body ?? {});
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("PATCH", path, body ?? {});
}

/** A whole-resource replace -- the coach's pricing is set as one object, not patched field by field. */
export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("PUT", path, body ?? {});
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>("DELETE", path);
}
